export interface PaginationInfo {
    totalItems: number;
    itemsPerPage: number;
    currentPage: number;
    totalPages: number;
    startIndex: number;
    endIndex: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  }
  
  export function getPaginationInfo(
    totalItems: number,
    itemsPerPage: number,
    currentPage: number
  ): PaginationInfo {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const validPage = Math.max(1, Math.min(currentPage, totalPages || 1));
    const startIndex = (validPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  
    return {
      totalItems,
      itemsPerPage,
      currentPage: validPage,
      totalPages,
      startIndex,
      endIndex,
      hasNextPage: validPage < totalPages,
      hasPrevPage: validPage > 1,
    };
  }
  
  export function getPaginatedItems<T>(
    items: T[],
    itemsPerPage: number,
    currentPage: number
  ): T[] {
    const { startIndex, endIndex } = getPaginationInfo(
      items.length,
      itemsPerPage,
      currentPage
    );
    return items.slice(startIndex, endIndex);
  }
  
  export function getPageNumbers(
    currentPage: number,
    totalPages: number,
    maxPagesToShow: number = 5
  ): (number | string)[] {
    if (totalPages <= maxPagesToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
  
    const pageNumbers: (number | string)[] = [];
    const halfWindow = Math.floor(maxPagesToShow / 2);
  
    let startPage = Math.max(1, currentPage - halfWindow);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
  
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
  
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push('...');
      }
    }
  
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
  
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      pageNumbers.push(totalPages);
    }
  
    return pageNumbers;
  }
  